import { isAppliedWildcardFilterEntry } from "./wildCardFilterUtils";

export const getAppliedFiltersCount = filterParams => {
    let filteredCount = 0;
    const isDefaultCspStatus = value => Array.isArray(value) && value.length === 1 && value[0] === "recommendation";
    if (
        (filterParams && Object.keys(filterParams)?.length) ||
        (filterParams?.exclude_filters && Object.keys(filterParams?.exclude_filters)?.length)
    ) {
        filteredCount =
            Object.entries(filterParams || filterParams?.exclude_filters)?.filter(
                ([key, value]) => value?.length && !["client_ids", "end_year", "csp_status"].includes(key)
            )?.length || 0;
        if (filterParams?.csp_status?.length && !isDefaultCspStatus(filterParams.csp_status)) {
            filteredCount += 1;
        }
        filteredCount +=
            (filterParams?.exclude_filters &&
                Object.entries(filterParams?.exclude_filters)?.filter(([key, value]) => value?.length && !["client_ids", "end_year"].includes(key))
                    ?.length) ||
            0;
        if (filterParams?.start_year || filterParams?.end_year) {
            filteredCount += 1;
        }
        if ("update_fci" in filterParams && filterParams.update_fci === false) {
            filteredCount += 1;
        }
        if (filterParams?.project_cost) {
            filteredCount += 1;
        }
        if (filterParams?.year) {
            filteredCount += 1;
        }
        if (filterParams.filters && Object.keys(filterParams.filters)?.length) {
            Object.values(filterParams.filters).forEach(value => {
                if (isAppliedWildcardFilterEntry(value)) {
                    filteredCount += 1;
                }
            });
        }
        if (filterParams.top_n && Object.keys(filterParams.top_n)?.length) {
            Object.values(filterParams.top_n).forEach(value => {
                if (value) {
                    filteredCount += 1;
                }
            });
        }
    }
    return filteredCount;
};

export const getAppliedWildCardFilterCount = filter => {
    let filterCount = 0;
    if (filter && Object.keys(filter)?.length) {
        Object.values(filter).forEach(value => {
            if (isAppliedWildcardFilterEntry(value)) {
                filterCount += 1;
            }
        });
    }
    return filterCount;
};

export const getAppliedListFilterCount = filter => {
    let filterCount = 0;
    const processFilter = obj => {
        if (obj && typeof obj === "object") {
            Object.values(obj).forEach(value => {
                if (Array.isArray(value) && value?.length > 0) {
                    filterCount += 1;
                } else if (typeof value === "object") {
                    // Recursive call for nested objects
                    processFilter(value);
                }
            });
        }
    };
    processFilter(filter);
    return filterCount;
};

export const flattenObject = (obj, prefix = "") => {
    let result = {};
    for (let key in obj) {
        if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
            const nestedKeys = flattenObject(obj[key], prefix + key + ".");
            result = { ...result, ...nestedKeys };
        } else if (Array.isArray(obj[key])) {
            result[prefix + key] = obj[key];
        } else {
            result[prefix + key] = obj[key];
        }
    }
    return result;
};

export const processDynamicFieldParams = params => {
    const dynamicFilters = {
        recommendations: {},
        buildings: {}
    };
    const newFilters = {};
    Object.keys(params?.filters || {}).forEach(key => {
        if (key.startsWith("recommendation_dynamic_fields.")) {
            const newKey = key.replace("recommendation_dynamic_fields.", "");
            dynamicFilters.recommendations[newKey] = params.filters[key];
        } else if (key.startsWith("building_dynamic_fields.")) {
            const newKey = key.replace("building_dynamic_fields.", "");
            dynamicFilters.buildings[newKey] = params.filters[key];
        } else {
            newFilters[key] = params.filters[key];
        }
    });

    const result = {
        ...params,
        filters: newFilters
    };
    result.dynamic_filters = {};
    if (Object.keys(dynamicFilters.recommendations).length > 0) {
        result.dynamic_filters.recommendations = dynamicFilters.recommendations;
    }
    if (Object.keys(dynamicFilters.buildings).length > 0) {
        result.dynamic_filters.buildings = dynamicFilters.buildings;
    }
    return result;
};

export const reverseDynamicFieldParams = processedParams => {
    const originalFilters = { ...processedParams.filters };

    if (processedParams.dynamic_filters?.recommendations) {
        Object.entries(processedParams.dynamic_filters.recommendations).forEach(([key, value]) => {
            originalFilters[`recommendation_dynamic_fields.${key}`] = value;
        });
    }

    if (processedParams.dynamic_filters?.buildings) {
        Object.entries(processedParams.dynamic_filters.buildings).forEach(([key, value]) => {
            originalFilters[`building_dynamic_fields.${key}`] = value;
        });
    }

    const originalParams = {
        ...processedParams,
        filters: originalFilters
    };

    delete originalParams.dynamic_filters;

    return originalParams;
};

export const hasActualFilter = filterData => {
    const isMeaningful = value => {
        if (typeof value === "boolean") return true;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "object" && value !== null) return Object.values(value).some(isMeaningful);
        return !!value;
    };

    const hasKeysWithValues = obj => {
        return Object.entries(obj)
            .filter(([key]) => key !== "exclude_filters")
            .some(([_, value]) => isMeaningful(value));
    };

    if (!filterData) return false;

    const hasExcludeFilters = filterData.exclude_filters ? isMeaningful(filterData.exclude_filters) : false;

    return hasKeysWithValues(filterData) || hasExcludeFilters;
};

/**
 * Strips UI-only keys from dashboard params before sending to the API.
 * apiType "map" retains reco_type; all others strip it along with
 * chart_view_type, dimension, topN, and sortOrder.
 */
export const getCleanParams = (params, apiType = "dashboard") => {
    const { chart_view_type, dimension, topN, sortOrder, reco_type, ...rest } = params || {};
    return apiType === "map" ? { ...rest, reco_type } : rest;
};

export { fieldMatchesFilterWildcardKey, getWildcardStorageKey } from "../components/dashboard/filterFieldBuilders";

/** Remove empty list-filter arrays */
export const stripEmptyListFilterParams = params => {
    if (!params || typeof params !== "object") return params;
    const result = { ...params };
    Object.keys(result).forEach(key => {
        if (key === "filters" || key === "top_n" || key === "exclude_filters") return;
        if (Array.isArray(result[key]) && result[key].length === 0) {
            delete result[key];
        }
    });
    if (result.exclude_filters && typeof result.exclude_filters === "object") {
        const exclude = { ...result.exclude_filters };
        Object.keys(exclude).forEach(key => {
            if (Array.isArray(exclude[key]) && exclude[key].length === 0) {
                delete exclude[key];
            }
        });
        if (Object.keys(exclude).length === 0) {
            delete result.exclude_filters;
        } else {
            result.exclude_filters = exclude;
        }
    }
    return result;
};
